/*
 * Module for the KB profile section
 */

define([
  "backbone",
  "jquery",
  "marionette",
  "messaging/messenger",
  "./kbProfileView",
  "./kbProfileModel",
  "modules/shared/adminMenu/adminMenuModule",
  "./kbSettings/kbSettingsModule",
  "./responseSettings/responseSettingsModule",
  "./responseStyles/responseStylesModule",
  "./kbDailyRefresh/kbDailyRefreshModule",
  "modules/shared/basicAdminList/basicAdminListModule",
  "./convertBackslash/convertBackslashModule",
  "./defaultUrl/defaultUrlModule",
  "./uiConfig/uiConfigModule",
  "./surveyLabels/surveyLabelsModule",
  "./cancelActionLabels/cancelActionLabelsModule",
  "./recommendations/recommendationsModule",
  "./advancedConfiguration/advancedConfigurationModule",
], function (
  Backbone,
  $,
  Marionette,
  Messenger,
  KbProfileView,
  KbProfileModel,
  AdminMenuModule,
  KbSettingsModule,
  ResponseSettingsModule,
  ResponseStylesModule,
  KbDailyRefreshModule,
  BasicAdminListModule,
  ConvertBackslashModule,
  DefaultUrlModule,
  UIConfigModule,
  SurveyLabelsModule,
  CancelActionLabelsModule,
  RecommendationsModule,
  AdvancedConfigurationModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "kbProfile";
      this.region = options.region;
      if (options.secretKeyExists) {
        //this means, it is an existing KB and the user is not adding a new KB, but editing existing KB
        this.secretKeyExists = options.secretKeyExists;
      } else {
        //user is adding a new KB
        this.secretKeyExists = false;
      }

      this._setUpVents();
    },
    show: function (options) {
      // TODO add landlordAdmin
      const tenantAdmin = Messenger.reqres.request("user:is:admin");
      const globalAdmin = Messenger.reqres.request("user:is:global:admin");
      if (!tenantAdmin && !globalAdmin) {
        // Only an admin is allowed to get here
        Backbone.history.navigate("#", { trigger: true });
        return;
      }
      let kb;
      const secretKeys = Messenger.reqres.request("kb:secretKeys:list");
      if (options.kb) {
        kb = options.kb;
        if (kb.isNew()) {
          Backbone.history.navigate("accountAdmin/kbs/new");
        } else {
          Backbone.history.navigate("accountAdmin/kbs/" + kb.id);
        }
      } else if (options.kbId) {
        const kbId = +options.kbId;
        const globalAdmin = Messenger.reqres.request("user:is:global:admin");
        let kbs;
        if (globalAdmin) {
          kbs = Messenger.reqres.request("admin:kbs");
        } else {
          const tenantId = Messenger.reqres.request("active:tenantId");
          kbs = Messenger.reqres.request("tenant:kbs", tenantId);
        }
        kbs.onSynced(function () {
          const kb = kbs.get(kbId);
          if (kb) {
            options.kb = kb;
            this.show(options);
          } else {
            Messenger.vent.trigger("show:account:admin:kbs");
          }
        }, this);
        return;
      }
      this.kb = kb;
      const kbSecretKey = secretKeys.findWhere({ kbId: kb.get("kbId") });
      kb.readOnly = !globalAdmin;
      this.layout = new KbProfileView.kbProfileLayout();
      this.sideMenu = this.addSubModule(new AdminMenuModule({ region: this.layout.sideMenu }));
      if (this.secretKeyExists) {
        this.kbSettings = this.addSubModule(
          new KbSettingsModule({
            region: this.layout.kbPane,
            model: kb,
            secretKey: kbSecretKey,
          }),
        );
      } else {
        this.kbSettings = this.addSubModule(
          new KbSettingsModule({ region: this.layout.kbPane, model: kb }),
        );
      }

      this.responseSettings = this.addSubModule(
        new ResponseSettingsModule({ region: this.layout.kbPane, model: kb }),
      );
      this.responseStyles = this.addSubModule(
        new ResponseStylesModule({ region: this.layout.kbPane, model: kb }),
      );
      this.dailyRefresh = this.addSubModule(
        new KbDailyRefreshModule({ region: this.layout.kbPane }),
      );
      this.contentAwareness = this.addSubModule(
        new BasicAdminListModule({
          region: this.layout.kbPane,
          areaModel: KbProfileModel.contentAwarenessModel,
        }),
      );
      this.convertBackslashes = this.addSubModule(
        new ConvertBackslashModule({ region: this.layout.kbPane }),
      );
      this.defaultUrl = this.addSubModule(new DefaultUrlModule({ region: this.layout.kbPane }));
      this.learnerStatuses = this.addSubModule(
        new BasicAdminListModule({
          region: this.layout.kbPane,
          areaModel: KbProfileModel.learnerStatusesModel,
        }),
      );
      this.privilegedAreas = this.addSubModule(
        new BasicAdminListModule({
          region: this.layout.kbPane,
          areaModel: KbProfileModel.privilegedAreaModel,
        }),
      );
      this.surveyLabelsModule = this.addSubModule(
        new SurveyLabelsModule({ region: this.layout.kbPane, model: kb }),
      );
      this.uiConfigModule = this.addSubModule(
        new UIConfigModule({ region: this.layout.kbPane, model: kb }),
      );
      this.taskTemplatesModule = this.addSubModule(
        new BasicAdminListModule({
          region: this.layout.kbPane,
          areaModel: KbProfileModel.taskTemplateModel,
        }),
      );
      if (kb.get("actionGroups")) {
        this.cancelActionLabelsModule = this.addSubModule(
          new CancelActionLabelsModule({
            region: this.layout.kbPane,
            model: kb,
          }),
        );
      }
      if (kb.get("recommendations")) {
        this.recommendationsModule = this.addSubModule(
          new RecommendationsModule({
            region: this.layout.kbPane,
            model: kb,
          }),
        );
      }
      this.addSubModule(
        new AdvancedConfigurationModule({
          region: this.layout.kbPane,
          model: kb,
        }),
      );
      this.layout.on(
        "show",
        function () {
          this._showSubViews();
        },
        this,
      );

      this.showRegion(this.layout);
    },
    _removeKb: function () {
      Messenger.confirmDialog(
        $.t("admin.kbAdmin.removeKbConfirm"),
        {
          confirm: (comment) => {
            if (comment && comment.toUpperCase() == "DELETE") {
              this.kb.deleteModel({
                success: () => {
                  Messenger.vent.trigger("show:account:admin:kbs");
                },
              });
            }
          },
        },
        this,
        "admin.kbAdmin.removeKb",
        {
          doNotModifyPlaceholder: true,
          placeholder: "common.delete",
          textArea: false,
        },
      );
    },
    _showSubViews: function () {
      const menuItems = Messenger.reqres.request("kb:admin:menu:items");
      const menuStyle = Messenger.reqres.request("kb:admin:menu:style");
      this.sideMenu.show(menuStyle, menuItems, "kb:profile");
    },
    _setUpVents: function () {
      //show the module
      Messenger.vent.on("show:admin:kbProfile", (options) => this.show(options));
      Messenger.vent.on("remove:kb", () => this._removeKb());
    },
  });
});
